<template>
  <div class="payroll-dashboard">
    <div class="page-header">
      <h1>{{ t('Payroll Dashboard') }}</h1>
    </div>

    <!-- Franchise Selector -->
    <box class="p-mb-3">
      <div class="franchise-selector">
        <label for="franchise">{{ t('Franquia') }}</label>
        <select-list
          id="franchise"
          elem-id="payroll_selectedFranchiseId"
          v-model="selectedFranchiseId"
          data-testid="payroll-dashboard-franchise-select"
          :options="[]"
          optionLabel="label"
          optionValue="value"
          :placeholder="t('Selecione a franquia')"
          @change="loadDuePayments"
        />
      </div>
    </box>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <spinner />
      <p>{{ t('Loading due payments...') }}</p>
    </div>

    <!-- Due Payments Cards -->
    <div v-else-if="duePayments && selectedFranchiseId" class="due-payments-grid">
      <box
        v-for="freq in availableFrequencies"
        :key="freq.frequency"
        class="frequency-card"
        :class="{
          'overdue': (freq.overdue_count ?? 0) > 0,
          'due-today': (freq.due_today_count ?? 0) > 0
        }"
      >
        <template #header>
          <div class="card-title">
            <span>{{ t(`${capitalizeFirst(freq.frequency)} Payroll`) }}</span>
            <Tag v-if="(freq.overdue_count ?? 0) > 0" severity="danger" class="p-ml-2">
              {{ t('Atrasado') }}: {{ freq.overdue_count }}
            </Tag>
            <Tag v-else-if="(freq.due_today_count ?? 0) > 0" severity="warning" class="p-ml-2">
              {{ t('Vence hoje') }}: {{ freq.due_today_count }}
            </Tag>
          </div>
        </template>

        <div class="card-details">
          <div class="detail-row">
            <i class="pi pi-calendar"></i>
            <span>{{ t('Período') }}: {{ freq.period_start }} - {{ freq.period_end }}</span>
          </div>

          <div class="detail-row">
            <i class="pi pi-users"></i>
            <span>{{ freq.employees_count }} {{ t('employees') }} • {{ freq.rules_count }} {{ t('rules') }}</span>
          </div>

          <div class="detail-row">
            <i class="pi pi-dollar"></i>
            <span>{{ t('Estimated Total') }}: {{ n(parseFloat(String(freq.estimated_total)) || 0, 'currency') }}</span>
          </div>

          <div v-if="freq.due_summary" class="due-summary">
            <Tag v-if="(freq.overdue_count ?? 0) > 0" severity="danger" class="p-mr-1">
              {{ freq.overdue_count }} {{ t('overdue') }}
            </Tag>
            <Tag v-if="(freq.due_today_count ?? 0) > 0" severity="warning">
              {{ freq.due_today_count }} {{ t('due today') }}
            </Tag>
          </div>

          <div v-if="canCreatePayrollPeriod" class="card-footer">
            <Button
              :data-testid="`payroll-dashboard-create-period-${freq.frequency}`"
              :label="t('Criar período')"
              icon="pi pi-plus"
              class="p-button-success"
              @click="createPeriod(freq.frequency)"
            />
          </div>
        </div>
      </box>

      <!-- No Due Payments -->
      <box v-if="availableFrequencies.length === 0" class="no-dues-card">
        <div class="no-dues-content">
          <i class="pi pi-check-circle"></i>
          <h3>{{ t('No Due Payments Found') }}</h3>
          <p>{{ t('Todas as regras de pagamento estão atualizadas') }}</p>
        </div>
      </box>
    </div>

    <!-- Recent Payrolls Section -->
    <box v-if="selectedFranchiseId" :title="t('Folhas recentes')" class="p-mt-4">
      <DataTable
        data-testid="payroll-dashboard-recent-periods-table"
        :value="recentPeriods"
        :paginator="false"
        :rows="5"
        responsiveLayout="scroll"
      >
        <Column field="name" :header="t('Nome')"></Column>
        <Column field="frequency" :header="t('Frequência')">
          <template #body="{ data }">
            {{ data.frequency }}
          </template>
        </Column>
        <Column field="status" :header="t('Status')">
          <template #body="{ data }">
            <Tag :severity="getStatusSeverity(data.status)">
              {{ t(capitalizeFirst(data.status)) }}
            </Tag>
          </template>
        </Column>
        <Column field="period_end_date" :header="t('Data')"></Column>
      </DataTable>
    </box>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { usePayrollCalculations } from '@/composables/usePayrollCalculations';
import { usePayrollPeriods } from '@/composables/usePayrollPeriods';
import { usePayrollPermissions } from '@/composables/usePayrollPermissions';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface DuePaymentFrequency {
  frequency: string;
  rules_count: number;
  employees_count?: number;
  overdue_count?: number;
  due_today_count?: number;
  period_start?: string;
  period_end?: string;
  estimated_total?: string | number;
  due_summary?: unknown;
  [key: string]: unknown;
}

interface PayrollPeriod {
  name?: string;
  frequency?: string;
  status?: string;
  period_end_date?: string;
  [key: string]: unknown;
}

const { t, n } = useI18n();
const router = useRouter();
const { discoverDuePayments, duePayments, isLoading } = usePayrollCalculations();
const { listPeriods } = usePayrollPeriods();
const { canCreatePayrollPeriod } = usePayrollPermissions();

const selectedFranchiseId = ref<Id | null>(null);
const recentPeriods = ref<PayrollPeriod[]>([]);

const availableFrequencies = computed((): DuePaymentFrequency[] => {
  if (!duePayments.value) return [];

  return Object.entries(duePayments.value as Record<string, Record<string, unknown>>)
    .map(([frequency, data]) => ({
      frequency,
      ...data
    } as DuePaymentFrequency))
    .filter(freq => freq.rules_count > 0);
});

onMounted(() => {
  log('PayrollDashboard', 'onMounted', 'Loading dashboard');
  loadDuePayments();
  loadRecentPeriods();
});

const loadDuePayments = async (): Promise<void> => {
  if (!selectedFranchiseId.value) return;

  log('PayrollDashboard', 'loadDuePayments', 'Loading due payments', {
    franchiseId: selectedFranchiseId.value
  });

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (discoverDuePayments as any)(selectedFranchiseId.value);
  } catch (err) {
    log('PayrollDashboard', 'loadDuePayments', 'Error loading due payments', err);
  }
};

const loadRecentPeriods = async (): Promise<void> => {
  if (!selectedFranchiseId.value) return;

  try {
    const periods = await listPeriods();
    recentPeriods.value = (periods as PayrollPeriod[]).slice(0, 5);
  } catch (err) {
    log('PayrollDashboard', 'loadRecentPeriods', 'Error loading recent periods', err);
  }
};

const createPeriod = (frequency: string): void => {
  router.push({
    name: 'PayrollPeriodCreate',
    query: {
      franchise_id: selectedFranchiseId.value ?? undefined,
      frequency
    }
  });
};

const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getStatusSeverity = (status: string): string => {
  const severities: Record<string, string> = {
    draft: 'info',
    calculated: 'info',
    paid: 'success',
    archived: 'secondary',
    reversed: 'danger'
  };
  return severities[status] || 'info';
};
</script>

<style scoped>
.payroll-dashboard {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
}

.franchise-selector {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.franchise-selector label {
  font-weight: 500;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.due-payments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.frequency-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.frequency-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.frequency-card.overdue {
  border-left: 4px solid var(--red-500);
}

.frequency-card.due-today {
  border-left: 4px solid var(--orange-500);
}

.card-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.card-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color-secondary);
}

.detail-row i {
  color: var(--primary-color);
}

.due-summary {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.no-dues-card {
  grid-column: 1 / -1;
}

.no-dues-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: var(--text-color-secondary);
}

.no-dues-content i {
  font-size: 3rem;
  color: var(--green-500);
  margin-bottom: 1rem;
}

.no-dues-content h3 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
}

.card-footer {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--surface-200);
}
</style>
